import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Measured content box (clientWidth/Height) of an element.
 *
 * - First measurement is synchronous (useLayoutEffect, pre-paint) so the very
 *   first layout pass already has real numbers — no fallback flash.
 * - Subsequent ResizeObserver updates are THROTTLED (trailing edge) so a
 *   window-drag recomputes downstream layout at a sane cadence instead of on
 *   every animation frame, while still settling on the final size.
 */
export function useMeasuredBox<T extends HTMLElement = HTMLDivElement>(throttleMs = 150) {
  const ref = useRef<T>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer: number | null = null;
    const apply = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      setBox(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    const onResize = () => {
      if (timer !== null) return; // trailing-edge throttle: one apply per window
      timer = window.setTimeout(() => {
        timer = null;
        apply();
      }, throttleMs);
    };
    apply();
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [throttleMs]);

  return { ref, ...box };
}
